import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Edit2, Trash2, Check, X, Users } from "lucide-react";
import type { Employee, TeamDefinition } from "../types";
import { useEmployees, useTeams, useEmployeesActions } from "../stores";
import { AddEmployeeModal } from "./components";
import { PageContainer, PageHeader, PageEmptyState } from "../components/PageLayout";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

export function EmployeesPage() {
  useDocumentTitle('Employees – qtplan');
  const { t } = useTranslation();
  const employees = useEmployees();
  const teams = useTeams();
  const { updateEmployee, deleteEmployee } = useEmployeesActions();

  const [showAddModal, setShowAddModal] = useState(false);

  // Group employees by team
  const employeesByTeam = teams.map((team) => ({
    team,
    employees: employees.filter((e) => e.team === team.id),
  }));

  // Employees without team
  const unassigned = employees.filter(
    (e) => !teams.some((t) => t.id === e.team)
  );

  return (
    <PageContainer wide>
      <PageHeader
        title={t('employees.title')}
        subtitle={t('employees.subtitle', { count: employees.length, teamCount: teams.length })}
        buttonLabel={t('employees.addButton')}
        onButtonClick={() => setShowAddModal(true)}
      />

      {employees.length === 0 ? (
        <PageEmptyState
          icon={<Users size={20} />}
          title={t('employees.noEmployees')}
          description={t('employees.noEmployeesDesc')}
        />
      ) : (
      <div className="space-y-4">
        {employeesByTeam.map(({ team, employees: teamEmployees }) => (
          <div key={team.id} className="bg-white rounded border border-gray-200 overflow-hidden">
            <div
              className="px-3 py-2 border-b flex items-center gap-2"
              style={{ borderLeftWidth: 3, borderLeftColor: team.color }}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: team.color }}
              />
              <h2 className="text-xs font-medium text-gray-900">{team.name}</h2>
              <span className="text-xs text-gray-500 ml-auto">
                {t('boards.employeeCount', { count: teamEmployees.length })}
              </span>
            </div>
            {teamEmployees.length === 0 ? (
              <div className="px-3 py-4 text-gray-400 text-xs italic">
                {t('employees.noEmployeesInTeam')}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {teamEmployees.map((employee) => (
                  <EmployeeRow
                    key={employee.id}
                    employee={employee}
                    teams={teams}
                    onUpdate={(updates) => updateEmployee(employee.id, updates)}
                    onDelete={() => deleteEmployee(employee.id)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {unassigned.length > 0 && (
          <div className="bg-white rounded border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-l-[3px] border-l-gray-300">
              <h2 className="text-xs font-medium text-gray-900">{t('employees.withoutTeam')}</h2>
              <span className="text-xs text-gray-500">
                {t('boards.employeeCount', { count: unassigned.length })}
              </span>
            </div>
            <div className="divide-y divide-gray-100">
              {unassigned.map((employee) => (
                <EmployeeRow
                  key={employee.id}
                  employee={employee}
                  teams={teams}
                  onUpdate={(updates) => updateEmployee(employee.id, updates)}
                  onDelete={() => deleteEmployee(employee.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      <AddEmployeeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={(name, team, color) => {
          // The modal handles adding via useEmployeesActions
        }}
      />
    </PageContainer>
  );
}

interface EmployeeRowProps {
  employee: Employee;
  teams: TeamDefinition[];
  onUpdate: (updates: Partial<Omit<Employee, "id">>) => void;
  onDelete: () => void;
}

function EmployeeRow({ employee, teams, onUpdate, onDelete }: EmployeeRowProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(employee.name);
  const [editTeam, setEditTeam] = useState(employee.team);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = () => {
    if (editName.trim()) {
      onUpdate({ name: editName.trim(), team: editTeam });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditName(employee.name);
    setEditTeam(employee.team);
    setIsEditing(false);
  };

  const currentTeam = teams.find((t) => t.id === employee.team);

  if (showDeleteConfirm) {
    return (
      <div className="px-3 py-1.5 bg-red-50 flex items-center gap-3">
        <span className="text-xs text-red-800">
          {t('employees.deleteConfirm', { name: employee.name })}
        </span>
        <div className="flex gap-1.5 ml-auto">
          <button
            onClick={() => {
              onDelete();
              setShowDeleteConfirm(false);
            }}
            className="text-xs text-red-600 border border-red-200 rounded px-2 py-0.5 hover:bg-red-100"
          >
            {t('employees.yesDelete')}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="text-xs text-gray-600 hover:bg-gray-200 rounded px-2 py-0.5"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="px-3 py-1.5 flex items-center gap-2">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0"
          style={{ backgroundColor: employee.color }}
        >
          {employee.name.charAt(0).toUpperCase()}
        </div>
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
          autoFocus
        />
        <select
          value={editTeam}
          onChange={(e) => setEditTeam(e.target.value)}
          className="px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
        >
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>
        <button
          onClick={handleSave}
          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
        >
          <Check size={14} />
        </button>
        <button
          onClick={handleCancel}
          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-1.5 flex items-center gap-2 hover:bg-gray-50 transition-colors">
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0"
        style={{ backgroundColor: employee.color }}
      >
        {employee.name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-900">{employee.name}</div>
        {currentTeam && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: currentTeam.color }}
            />
            {currentTeam.name}
          </div>
        )}
      </div>
      <button
        onClick={() => setIsEditing(true)}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        title={t('common.edit')}
      >
        <Edit2 size={14} />
      </button>
      <button
        onClick={() => setShowDeleteConfirm(true)}
        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
        title={t('common.delete')}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}
